import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { useGetPartnersCarrierPartnerships } from 'utils/web_api'
import type { WebAPIPartnersEntitiesCarrierPartnership } from 'utils/web_api.schemas'

// The API returns the carrier partnership wrapped in an object
interface CarrierPartnershipResponse {
  carrier_partnership: WebAPIPartnersEntitiesCarrierPartnership | null
}

interface CarrierBrandingContextValue {
  carrierPartnership: WebAPIPartnersEntitiesCarrierPartnership | null
  isLoading: boolean
  hasCarrierBranding: boolean
}

const CarrierBrandingContext = createContext<CarrierBrandingContextValue | null>(null)

interface CarrierBrandingProviderProps {
  children: ReactNode
  state: string | undefined | null
}

/**
 * Provider that fetches and provides carrier partnership branding data based on the employer's state.
 * When a partner admin is opted into a carrier partnership for the given state, the carrier's
 * branding (logo, name) will be available through the useCarrierBranding hook.
 */
export function CarrierBrandingProvider ({ children, state }: CarrierBrandingProviderProps) {
  const { data, isLoading } = useGetPartnersCarrierPartnerships<CarrierPartnershipResponse>(
    { state: state ?? '' },
    {
      query: {
        enabled: Boolean(state),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      }
    }
  )

  const value = useMemo<CarrierBrandingContextValue>(() => {
    const carrierPartnership = data?.carrier_partnership ?? null
    return {
      carrierPartnership,
      isLoading: Boolean(state) && isLoading,
      hasCarrierBranding: carrierPartnership !== null
    }
  }, [data, isLoading, state])

  return (
    <CarrierBrandingContext.Provider value={value}>
      {children}
    </CarrierBrandingContext.Provider>
  )
}

/**
 * Hook to access carrier branding data from the nearest CarrierBrandingProvider.
 * Returns null if used outside of a CarrierBrandingProvider.
 */
export function useCarrierBranding (): CarrierBrandingContextValue | null {
  return useContext(CarrierBrandingContext)
}

/**
 * Hook that throws if used outside of a CarrierBrandingProvider.
 * Use this when you expect branding to always be available.
 */
export function useCarrierBrandingRequired (): CarrierBrandingContextValue {
  const context = useContext(CarrierBrandingContext)
  if (context === null) {
    throw new Error('useCarrierBrandingRequired must be used within a CarrierBrandingProvider')
  }
  return context
}
